import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import DemoModal from './DemoModal';

const products = [
  {
    title: 'Sultansoft Academy',
    tagline: 'Empowering Africa\'s Next Generation of Tech Talent',
    description: 'A comprehensive online learning platform offering industry-relevant courses, certifications, and mentorship programs designed specifically for the African tech ecosystem.',
    features: [
      'Interactive coding challenges',
      'Industry mentorship programs',
      'African-focused curriculum',
      'Job placement assistance'
    ],
    icon: (
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      </svg>
    ),
    gradient: 'from-crimson-500 to-crimson-600',
    status: 'Live',
    cta: 'Visit Academy',
    link: 'https://academy.sultansoft.com',
    external: true,
  },
  {
    title: 'Shaka',
    tagline: 'Democratizing African Investment',
    description: 'A Robinhood-inspired trading platform focused on the Nigerian Stock Exchange and cryptocurrency markets, making investment accessible to all Africans.',
    features: [
      'Zero-commission trading',
      'Real-time market data',
      'Nigerian Stock Exchange integration',
      'Cryptocurrency trading'
    ],
    icon: (
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    gradient: 'from-coral-500 to-orange-500',
    status: 'Coming Soon',
    cta: 'Join Waitlist',
  },
  {
    title: 'Blockchain & Telco Solutions',
    tagline: 'Enterprise Web3 & Telecommunications Infrastructure',
    description: 'We provide cutting-edge blockchain, Web3, and telecommunications solutions for enterprises. Our world-class team of experienced engineers delivers high-quality, custom solutions tailored to your business needs.',
    features: [
      'Blockchain development & consulting',
      'Web3 application development',
      'Enterprise telecommunications',
      'Custom solution architecture'
    ],
    icon: (
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
      </svg>
    ),
    gradient: 'from-navy-500 to-navy-600',
    status: 'Available',
    cta: 'Request Demo',
    demo: true,
  },
];

function ProductCard({ product, index, onDemoClick }) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.2 }}
      className="relative group"
    >
      <div className="relative z-10 h-full p-8 bg-white rounded-3xl shadow-xl transition-all duration-300 group-hover:shadow-2xl border border-gray-100 group-hover:border-gray-200">
        <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300 ${product.gradient}`} />
        
        <div className="relative">
          {/* Status Badge */}
          <div className="flex justify-between items-start mb-6">
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              product.status === 'Available' 
                ? 'bg-green-100 text-green-800' 
                : product.status === 'In Development'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {product.status}
            </div>
          </div>

          {/* Icon */}
          <div className={`w-16 h-16 mb-6 bg-gradient-to-r ${product.gradient} rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}>
            {product.icon}
          </div>
          
          {/* Title and Tagline */}
          <h3 className="text-2xl font-display font-bold text-navy-950 mb-2">{product.title}</h3>
          <p className="text-sm font-medium text-gray-500 mb-4">{product.tagline}</p>
          
          {/* Description */}
          <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>
          
          {/* Features */}
          <div className="mb-8">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Features:</h4>
            <ul className="space-y-2">
              {product.features.map((feature, featureIndex) => (
                <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          
          {/* CTA Button */}
          <motion.div 
            className="mt-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {product.external ? (
              <a 
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn btn-primary"
              >
                {product.cta}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            ) : product.demo ? (
              <button 
                onClick={onDemoClick}
                className="w-full btn btn-primary"
              >
                {product.cta}
              </button>
            ) : (
              <button className={`w-full btn ${
                product.status === 'Available' 
                  ? 'btn-primary' 
                  : 'btn-secondary'
              }`}>
                {product.cta}
              </button>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

function Products() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  return (
    <section id="products" className="section bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto mb-20"
        >
          <h2 className="heading text-navy-950 mb-6">
            Our Products
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Three flagship solutions designed to transform how Africa learns, invests, and communicates. 
            Each product addresses a critical need in the African digital ecosystem.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {products.map((product, index) => (
            <ProductCard 
              key={index} 
              product={product} 
              index={index} 
              onDemoClick={() => setIsDemoModalOpen(true)}
            />
          ))}
        </div>

        {/* Product Roadmap */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-navy-950 rounded-3xl p-12 text-center text-white"
        >
          <h3 className="text-3xl font-display font-bold mb-6">
            Product Roadmap
          </h3>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            We're building the future of African technology, one product at a time. 
            Our roadmap reflects our commitment to addressing the continent's most pressing digital needs.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-crimson-500 to-crimson-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-display font-bold mb-2">Live Now</h4>
              <p className="text-gray-300">Sultansoft Academy</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-coral-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="text-lg font-display font-bold mb-2">Q2 2025</h4>
              <p className="text-gray-300">Shaka Trading Platform</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-navy-500 to-navy-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
              </div>
              <h4 className="text-lg font-display font-bold mb-2">Available Now</h4>
              <p className="text-gray-300">Blockchain & Telco Solutions</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Demo Modal */}
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
    </section>
  );
}

export default Products;